class MWDrawer {
  constructor(root) {
    this.root = root;
    this.name = root.dataset.mwDrawer;
    this.triggers = document.querySelectorAll(`[data-mw-open="${this.name}"]`);
    this.closers = root.querySelectorAll("[data-mw-close]");
    this.lastFocus = null;
    this.handleKeydown = this.handleKeydown.bind(this);
    this.bind();
  }

  bind() {
    this.triggers.forEach((trigger) => {
      trigger.addEventListener("click", (event) => {
        event.preventDefault();
        this.open(trigger);
      });
    });

    this.closers.forEach((closer) => {
      closer.addEventListener("click", () => this.close());
    });

    document.addEventListener("keydown", this.handleKeydown);
  }

  handleKeydown(event) {
    if (!this.root.classList.contains("is-open")) return;

    if (event.key === "Escape") {
      this.close();
      return;
    }

    if (event.key !== "Tab") return;

    const focusable = this.focusable();
    if (!focusable.length) {
      event.preventDefault();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  open(trigger) {
    MWDrawer.closeAll(this.name);
    this.lastFocus = trigger || document.activeElement;
    this.root.classList.add("is-open");
    this.root.removeAttribute("aria-hidden");
    document.body.classList.add("mw-drawer-open");
    this.triggers.forEach((item) => item.setAttribute("aria-expanded", "true"));

    const focusTarget = this.root.querySelector("[data-mw-autofocus]") || this.root.querySelector("button, a, input, select, textarea");
    if (focusTarget) window.setTimeout(() => focusTarget.focus(), 80);
  }

  close(restoreFocus = true) {
    this.root.classList.remove("is-open");
    this.root.setAttribute("aria-hidden", "true");
    this.triggers.forEach((item) => item.setAttribute("aria-expanded", "false"));

    if (!document.querySelector(".mw-overlay.is-open")) {
      document.body.classList.remove("mw-drawer-open");
    }

    if (restoreFocus && this.lastFocus && typeof this.lastFocus.focus === "function") {
      this.lastFocus.focus();
    }
  }

  focusable() {
    return Array.from(this.root.querySelectorAll([
      "a[href]",
      "button:not([disabled])",
      "input:not([disabled])",
      "select:not([disabled])",
      "textarea:not([disabled])",
      "[tabindex]:not([tabindex='-1'])"
    ].join(","))).filter((item) => item.offsetParent !== null);
  }

  static closeAll(exceptName) {
    document.querySelectorAll(".mw-overlay.is-open").forEach((drawer) => {
      if (drawer.dataset.mwDrawer !== exceptName) {
        if (drawer.mwDrawer) drawer.mwDrawer.close(false);
        else {
          drawer.classList.remove("is-open");
          drawer.setAttribute("aria-hidden", "true");
        }
      }
    });
  }
}

class MWHeader {
  constructor(root) {
    this.root = root;
    this.onScroll = this.onScroll.bind(this);
    window.addEventListener("scroll", this.onScroll, { passive: true });
    this.onScroll();
  }

  onScroll() {
    this.root.classList.toggle("is-scrolled", window.scrollY > 8);
  }
}

class MWMegaNav {
  constructor(root) {
    this.root = root;
    this.items = Array.from(root.querySelectorAll("[data-mw-mega-item]"));
    this.closeTimer = null;
    this.handleKeydown = this.handleKeydown.bind(this);
    this.bind();
  }

  bind() {
    this.items.forEach((item) => {
      item.addEventListener("mouseenter", () => this.open(item));
      item.addEventListener("mouseleave", () => this.scheduleClose(item));
      item.addEventListener("focusin", () => this.open(item));
      item.addEventListener("focusout", (event) => {
        if (!item.contains(event.relatedTarget)) this.close(item);
      });

      const trigger = item.querySelector(".mw-header-nav__trigger");
      if (trigger) {
        trigger.addEventListener("click", (event) => {
          const hoverCapable = window.matchMedia("(hover: hover)").matches;
          if (!hoverCapable && !item.classList.contains("is-open")) {
            event.preventDefault();
            this.open(item);
          }
        });
      }
    });

    document.addEventListener("keydown", this.handleKeydown);
    window.addEventListener("resize", this.debounce(() => this.closeAll(), 150));
  }

  open(item) {
    window.clearTimeout(this.closeTimer);
    this.items.forEach((other) => {
      if (other !== item) this.setState(other, false);
    });
    this.setState(item, true);
  }

  scheduleClose(item) {
    window.clearTimeout(this.closeTimer);
    this.closeTimer = window.setTimeout(() => this.close(item), 150);
  }

  close(item) {
    this.setState(item, false);
  }

  closeAll() {
    this.items.forEach((item) => this.setState(item, false));
  }

  setState(item, isOpen) {
    const trigger = item.querySelector(".mw-header-nav__trigger");
    const panel = item.querySelector(".mw-mega");
    if (!trigger || !panel) return;
    item.classList.toggle("is-open", isOpen);
    trigger.setAttribute("aria-expanded", isOpen ? "true" : "false");
    panel.setAttribute("aria-hidden", isOpen ? "false" : "true");
  }

  handleKeydown(event) {
    if (event.key !== "Escape") return;
    const openItem = this.items.find((item) => item.classList.contains("is-open"));
    if (!openItem) return;
    this.setState(openItem, false);
    const trigger = openItem.querySelector(".mw-header-nav__trigger");
    if (trigger) trigger.focus();
  }

  debounce(callback, wait) {
    let timer;
    return () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(callback, wait);
    };
  }
}

class MWHeroSlider {
  constructor(root) {
    this.root = root;
    this.track = root.querySelector("[data-mw-hero-track]");
    this.slides = Array.from(root.querySelectorAll("[data-mw-hero-slide]"));
    this.dots = root.querySelector("[data-mw-hero-dots]");
    this.prevButton = root.querySelector("[data-mw-hero-prev]");
    this.nextButton = root.querySelector("[data-mw-hero-next]");
    this.index = 0;
    this.timer = null;
    this.reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    this.autoplay = root.dataset.autoplay === "true";
    this.interval = parseInt(root.dataset.interval, 10) || 6000;
    this.startX = null;

    if (!this.track || this.slides.length < 2) return;
    this.bind();
    this.render();
    this.start();
  }

  bind() {
    if (this.dots) {
      this.dots.innerHTML = "";
      this.slides.forEach((_, index) => {
        const dot = document.createElement("button");
        dot.type = "button";
        dot.setAttribute("aria-label", `Go to slide ${index + 1}`);
        dot.addEventListener("click", () => this.go(index));
        this.dots.appendChild(dot);
      });
    }

    if (this.prevButton) this.prevButton.addEventListener("click", () => this.prev());
    if (this.nextButton) this.nextButton.addEventListener("click", () => this.next());

    this.track.addEventListener("touchstart", (event) => {
      this.startX = event.touches[0].clientX;
    }, { passive: true });

    this.track.addEventListener("touchend", (event) => {
      if (this.startX === null) return;
      const delta = event.changedTouches[0].clientX - this.startX;
      if (Math.abs(delta) > 42) {
        delta < 0 ? this.next() : this.prev();
      }
      this.startX = null;
    });

    this.root.addEventListener("mouseenter", () => this.stop());
    this.root.addEventListener("mouseleave", () => this.start());
  }

  render() {
    this.track.style.transform = `translateX(-${this.index * 100}%)`;
    if (this.dots) {
      Array.from(this.dots.children).forEach((dot, index) => {
        dot.setAttribute("aria-current", index === this.index ? "true" : "false");
      });
    }
  }

  go(index) {
    this.index = (index + this.slides.length) % this.slides.length;
    this.render();
    this.restart();
  }

  next() {
    this.go(this.index + 1);
  }

  prev() {
    this.go(this.index - 1);
  }

  start() {
    if (!this.autoplay || this.reducedMotion) return;
    this.stop();
    this.timer = window.setInterval(() => this.next(), this.interval);
  }

  stop() {
    window.clearInterval(this.timer);
  }

  restart() {
    this.stop();
    this.start();
  }
}

class MWDragRail {
  constructor(root) {
    this.root = root;
    this.down = false;
    this.startX = 0;
    this.startScroll = 0;
    this.bind();
  }

  bind() {
    this.root.addEventListener("pointerdown", (event) => {
      this.down = true;
      this.startX = event.clientX;
      this.startScroll = this.root.scrollLeft;
      this.root.classList.add("is-dragging");
      this.root.setPointerCapture(event.pointerId);
    });

    this.root.addEventListener("pointermove", (event) => {
      if (!this.down) return;
      this.root.scrollLeft = this.startScroll - (event.clientX - this.startX);
    });

    ["pointerup", "pointercancel", "pointerleave"].forEach((type) => {
      this.root.addEventListener(type, () => {
        this.down = false;
        this.root.classList.remove("is-dragging");
      });
    });
  }
}

class MWAccordion {
  constructor(root) {
    this.root = root;
    this.buttons = root.querySelectorAll("[data-mw-accordion-button]");
    this.bind();
  }

  bind() {
    this.buttons.forEach((button) => {
      button.addEventListener("click", () => this.toggle(button));
      if (button.getAttribute("aria-expanded") === "true") this.open(button, false);
    });
  }

  toggle(button) {
    const expanded = button.getAttribute("aria-expanded") === "true";
    if (expanded) this.close(button);
    else this.open(button);
  }

  open(button) {
    const panel = document.getElementById(button.getAttribute("aria-controls"));
    if (!panel) return;
    button.setAttribute("aria-expanded", "true");
    panel.style.maxHeight = `${panel.scrollHeight}px`;
  }

  close(button) {
    const panel = document.getElementById(button.getAttribute("aria-controls"));
    if (!panel) return;
    button.setAttribute("aria-expanded", "false");
    panel.style.maxHeight = "0px";
  }
}

class MWTabs {
  constructor(root) {
    this.root = root;
    this.tabs = Array.from(root.querySelectorAll("[data-mw-tab]"));
    this.bind();

    const selected = this.tabs.find((tab) => tab.getAttribute("aria-selected") === "true");
    if (selected || this.tabs[0]) this.activate(selected || this.tabs[0]);
    root.dataset.ready = "true";
  }

  bind() {
    this.tabs.forEach((tab) => {
      tab.addEventListener("click", () => this.activate(tab));
      tab.addEventListener("keydown", (event) => this.onKeydown(event, tab));
    });
  }

  onKeydown(event, tab) {
    const keys = { ArrowDown: 1, ArrowRight: 1, ArrowUp: -1, ArrowLeft: -1 };
    if (!(event.key in keys)) return;
    event.preventDefault();
    const step = keys[event.key];
    const index = (this.tabs.indexOf(tab) + step + this.tabs.length) % this.tabs.length;
    this.activate(this.tabs[index]);
    this.tabs[index].focus();
  }

  activate(tab) {
    const target = tab.dataset.mwTab;
    this.tabs.forEach((item) => {
      const active = item === tab;
      item.setAttribute("aria-selected", active ? "true" : "false");
      item.tabIndex = active ? 0 : -1;
    });
    this.root.querySelectorAll("[data-mw-tab-panel]").forEach((panel) => {
      panel.hidden = panel.dataset.mwTabPanel !== target;
    });
  }

  activateById(id) {
    const tab = this.tabs.find((item) => item.dataset.mwTab === id);
    if (tab) this.activate(tab);
  }
}

// PDP info tabs rendered as an accordion. The first item always starts open.
class MWProductAccordion {
  constructor(root) {
    this.root = root;
    this.multiple = root.dataset.multiple !== "false";
    this.items = Array.from(root.children).filter((item) => item.matches("[data-mw-acc-item]"));
    this.items.forEach((item, index) => {
      const trigger = item.querySelector("[data-mw-acc-trigger]");
      if (trigger) trigger.addEventListener("click", () => this.set(item, !this.isOpen(item)));
      this.set(item, index === 0);
    });
    root.dataset.ready = "true";
  }

  isOpen(item) {
    return item.classList.contains("is-open");
  }

  set(item, open) {
    if (open && !this.multiple) {
      this.items.forEach((other) => {
        if (other !== item && this.isOpen(other)) this.set(other, false);
      });
    }
    item.classList.toggle("is-open", open);
    const trigger = item.querySelector("[data-mw-acc-trigger]");
    const panel = item.querySelector(".mw-acc__panel");
    if (trigger) trigger.setAttribute("aria-expanded", open ? "true" : "false");
    // Closed content stays out of the tab order and away from screen readers.
    if (panel) panel.toggleAttribute("inert", !open);
  }

  open(item) {
    if (this.items.includes(item)) this.set(item, true);
  }
}

class MWQuantity {
  constructor(root) {
    this.root = root;
    this.input = root.querySelector("input");
    this.bind();
  }

  bind() {
    this.root.addEventListener("click", (event) => {
      const button = event.target.closest("[data-mw-qty]");
      if (!button || !this.input) return;
      const change = button.dataset.mwQty === "plus" ? 1 : -1;
      const next = Math.max(1, (parseInt(this.input.value, 10) || 1) + change);
      this.input.value = next;
      this.input.dispatchEvent(new Event("change", { bubbles: true }));
    });
  }
}

class MWProductGallery {
  constructor(root) {
    this.root = root;
    this.main = root.querySelector("[data-mw-gallery-main]");
    this.thumbs = root.querySelectorAll("[data-mw-gallery-thumb]");
    this.bind();
  }

  bind() {
    this.thumbs.forEach((thumb) => {
      thumb.addEventListener("click", () => {
        if (!this.main) return;
        this.thumbs.forEach((item) => item.setAttribute("aria-current", "false"));
        thumb.setAttribute("aria-current", "true");
        this.main.src = thumb.dataset.mwGalleryThumb;
        if (thumb.dataset.mwGallerySrcset) this.main.srcset = thumb.dataset.mwGallerySrcset;
      });
    });
  }
}

class MWProductForm {
  constructor(root) {
    this.root = root;
    this.form = root.querySelector("[data-mw-product-form]");
    this.variantInput = root.querySelector("[data-mw-variant-id]");
    this.price = root.querySelector("[data-mw-product-price]");
    this.compare = root.querySelector("[data-mw-product-compare]");
    this.submit = root.querySelector("[data-mw-product-submit]");
    this.galleryMain = root.querySelector("[data-mw-gallery-main]");
    this.variants = this.readVariants();
    this.moneyFormat = root.dataset.moneyFormat || "${{amount}}";
    this.bind();
    this.update();
  }

  readVariants() {
    const script = this.root.querySelector("[data-mw-product-variants]");
    if (!script) return [];
    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      return [];
    }
  }

  bind() {
    this.root.querySelectorAll("[data-mw-option]").forEach((input) => {
      input.addEventListener("change", () => this.update());
    });
  }

  selectedOptions() {
    return Array.from(this.root.querySelectorAll("[data-mw-option]:checked"))
      .sort((a, b) => Number(a.dataset.optionIndex) - Number(b.dataset.optionIndex))
      .map((input) => input.value);
  }

  currentVariant() {
    const selected = this.selectedOptions();
    return this.variants.find((variant) => {
      return selected.every((value, index) => variant.options[index] === value);
    });
  }

  update() {
    const variant = this.currentVariant() || this.variants[0];
    if (!variant) return;

    if (this.variantInput) this.variantInput.value = variant.id;
    if (this.price) this.setMoney(this.price, variant.price);

    if (this.compare) {
      const showCompare = variant.compare_at_price && variant.compare_at_price > variant.price;
      this.compare.hidden = !showCompare;
      if (showCompare) this.setMoney(this.compare, variant.compare_at_price);
    }

    if (this.submit) {
      this.submit.disabled = !variant.available;
      this.submit.textContent = variant.available ? this.submit.dataset.availableText : this.submit.dataset.soldText;
    }

    const image = variant.featured_image;
    if (this.galleryMain && image && image.src) {
      this.galleryMain.src = image.src;
      this.galleryMain.removeAttribute("srcset");
    }
  }

  setMoney(element, cents) {
    element.innerHTML = this.formatMoney(cents);
  }

  formatMoney(cents) {
    if (window.Shopify && typeof window.Shopify.formatMoney === "function") {
      return window.Shopify.formatMoney(cents, this.moneyFormat);
    }

    const amount = (Number(cents || 0) / 100).toFixed(2);
    return this.moneyFormat
      .replace(/\{\{\s*amount\s*\}\}/, amount)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(Number(amount)).toString());
  }
}

class MWProductCard {
  constructor(root) {
    this.root = root;
    this.image = root.querySelector("[data-mw-product-card-image]");
    this.price = root.querySelector("[data-mw-product-card-price]");
    this.compare = root.querySelector("[data-mw-product-card-compare]");
    this.variantInput = root.querySelector("[data-mw-product-card-variant-input]");
    this.submit = root.querySelector("[data-mw-product-card-submit]");
    this.quick = root.querySelector(".mw-product-card__quick");
    this.swatches = Array.from(root.querySelectorAll("[data-mw-product-card-swatch]"));
    this.moneyFormat = root.dataset.moneyFormat || (window.theme && window.theme.moneyFormat) || "${{amount}}";
    this.bind();
  }

  bind() {
    this.swatches.forEach((swatch) => {
      swatch.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.select(swatch);
      });
    });

    if (this.quick) {
      this.quick.addEventListener("click", (event) => {
        event.stopPropagation();
      });
    }
  }

  select(swatch) {
    const variantId = swatch.dataset.variantId;
    if (!variantId) return;

    this.swatches.forEach((item) => item.setAttribute("aria-pressed", item === swatch ? "true" : "false"));

    if (this.variantInput) this.variantInput.value = variantId;
    this.updateMedia(swatch);
    this.updatePrice(swatch);
    this.updateSubmit(swatch);
  }

  updateMedia(swatch) {
    if (!this.image || !swatch.dataset.imageSrc) return;

    this.image.src = swatch.dataset.imageSrc;
    if (swatch.dataset.imageSrcset) this.image.srcset = swatch.dataset.imageSrcset;
    else this.image.removeAttribute("srcset");

    if (swatch.dataset.imageAlt) this.image.alt = swatch.dataset.imageAlt;
  }

  updatePrice(swatch) {
    const price = Number(swatch.dataset.price || 0);
    const compareAtPrice = Number(swatch.dataset.compareAtPrice || 0);

    if (this.price && price) this.setMoney(this.price, price);

    if (!this.compare) return;
    const showCompare = compareAtPrice > price;
    this.compare.hidden = !showCompare;
    if (showCompare) this.setMoney(this.compare, compareAtPrice);
  }

  updateSubmit(swatch) {
    if (!this.submit) return;

    const available = swatch.dataset.available === "true";
    this.submit.disabled = !available;
    this.submit.textContent = available ? this.submit.dataset.availableText : this.submit.dataset.soldText;
  }

  setMoney(element, cents) {
    element.innerHTML = this.formatMoney(cents);
  }

  formatMoney(cents) {
    if (window.Shopify && typeof window.Shopify.formatMoney === "function") {
      return window.Shopify.formatMoney(cents, this.moneyFormat);
    }

    const amount = (Number(cents || 0) / 100).toFixed(2);
    return this.moneyFormat
      .replace(/\{\{\s*amount\s*\}\}/, amount)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(Number(amount)).toString());
  }
}

class MWCartDrawer {
  constructor(root) {
    this.root = root;
    this.content = root.querySelector("[data-mw-cart-content]");
    this.message = root.querySelector("[data-mw-cart-message]");
    this.sectionId = root.dataset.sectionId || "mw-side-cart";
    this.bind();
  }

  bind() {
    document.addEventListener("submit", (event) => {
      const form = event.target.closest("form");
      if (!form || !this.isAddForm(form)) return;
      if (event.submitter && event.submitter.closest(".shopify-payment-button")) return;
      event.preventDefault();
      this.add(form, event.submitter);
    });

    this.root.addEventListener("click", (event) => {
      const control = event.target.closest("[data-mw-cart-change]");
      if (!control) return;
      event.preventDefault();
      this.change(control.dataset.mwCartChange, Number(control.dataset.quantity));
    });
  }

  isAddForm(form) {
    const action = form.getAttribute("action") || "";
    return action.includes("/cart/add");
  }

  route(path) {
    const root = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : "/";
    return `${root.replace(/\/$/, "")}${path}`;
  }

  headerSectionId() {
    const header = document.querySelector("[data-mw-header]");
    return (header && header.dataset.sectionId) || "mw-header";
  }

  sectionsToRender() {
    return [this.sectionId, this.headerSectionId()];
  }

  async add(form, submitter) {
    this.setLoading(true);
    this.clearMessage();
    if (submitter) submitter.disabled = true;

    try {
      const formData = new FormData(form);
      formData.append("sections", this.sectionsToRender());
      formData.append("sections_url", window.location.pathname);

      const response = await fetch(this.route("/cart/add.js"), {
        method: "POST",
        body: formData,
        headers: { Accept: "application/json" }
      });

      const payload = await response.json();
      if (!response.ok) throw payload;

      await this.renderSections(payload.sections);
      this.notify();
      this.showMessage(this.root.dataset.addedMessage || "Added to cart.", "success");
      this.open();
    } catch (error) {
      this.showMessage(error.description || error.message || this.root.dataset.errorMessage || "We could not add this item to cart.", "error");
    } finally {
      this.setLoading(false);
      if (submitter) submitter.disabled = false;
    }
  }

  async change(key, quantity) {
    this.setLoading(true);
    this.clearMessage();

    try {
      const response = await fetch(this.route("/cart/change.js"), {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          id: key,
          quantity,
          sections: this.sectionsToRender(),
          sections_url: window.location.pathname
        })
      });

      const payload = await response.json();
      if (!response.ok) throw payload;

      await this.renderSections(payload.sections);
      this.notify();
    } catch (error) {
      this.showMessage(error.description || error.message || this.root.dataset.errorMessage || "We could not update the cart.", "error");
    } finally {
      this.setLoading(false);
    }
  }

  // Shopify Section Rendering: el HTML del drawer y del contador del header
  // siempre viene de las sections Liquid, nunca se reconstruye a mano en JS.
  async renderSections(sections) {
    if (!sections) {
      await Promise.all([
        this.refreshCartSection(),
        this.refreshHeaderSection()
      ]);
      return;
    }

    this.applyCartSection(sections[this.sectionId]);
    this.applyHeaderSection(sections[this.headerSectionId()]);
  }

  applyCartSection(html) {
    if (!html || !this.content) return;
    const fragment = this.parseSection(html).querySelector("[data-mw-cart-content]");
    if (fragment) this.content.innerHTML = fragment.innerHTML;
  }

  applyHeaderSection(html) {
    if (!html) return;
    const source = this.parseSection(html).querySelector("[data-mw-cart-count]");
    if (!source) return;

    document.querySelectorAll("[data-mw-cart-count]").forEach((item) => {
      item.textContent = source.textContent;
      item.setAttribute("aria-label", source.getAttribute("aria-label") || "");
    });
  }

  async refreshCartSection() {
    const html = await this.fetchSection(this.sectionId);
    if (html) this.applyCartSection(html);
  }

  async refreshHeaderSection() {
    const html = await this.fetchSection(this.headerSectionId());
    if (html) this.applyHeaderSection(html);
  }

  async fetchSection(sectionId) {
    try {
      const response = await fetch(this.route(`/cart?section_id=${encodeURIComponent(sectionId)}`));
      if (!response.ok) return null;
      return await response.text();
    } catch (error) {
      return null;
    }
  }

  parseSection(html) {
    return new DOMParser().parseFromString(html, "text/html");
  }

  // Lets other cart UIs (e.g. the cart page) know the cart changed from the drawer.
  notify() {
    document.dispatchEvent(new CustomEvent("mw:cart:updated", { detail: { source: "drawer" } }));
  }

  open() {
    const drawer = this.root.mwDrawer;
    if (drawer) drawer.open(document.querySelector('[data-mw-open="cart"]'));
  }

  setLoading(isLoading) {
    this.root.classList.toggle("mw-cart-is-loading", isLoading);
  }

  showMessage(message, type = "error") {
    if (!this.message) return;
    this.message.textContent = message;
    this.message.dataset.state = type;
    this.message.hidden = false;
  }

  clearMessage() {
    if (!this.message) return;
    this.message.textContent = "";
    this.message.hidden = true;
  }
}

// Cart page: same Section Rendering approach as the drawer. Every mutation asks
// Shopify for the cart page, drawer and header sections so all three stay in sync.
class MWCartPage {
  constructor(root) {
    this.root = root;
    this.sectionId = root.dataset.sectionId;
    this.noteTimer = null;
    this.bind();
  }

  get content() {
    return this.root.querySelector("[data-mw-cart-page-content]");
  }

  drawer() {
    const element = document.querySelector("[data-mw-cart-root]");
    return element ? element.mwCart : null;
  }

  route(path) {
    const root = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : "/";
    return `${root.replace(/\/$/, "")}${path}`;
  }

  sectionsToRender() {
    const ids = [this.sectionId];
    const drawer = this.drawer();
    if (drawer) ids.push(...drawer.sectionsToRender());
    return Array.from(new Set(ids));
  }

  bind() {
    this.root.addEventListener("click", (event) => {
      const change = event.target.closest("[data-mw-cart-change]");
      if (change) {
        event.preventDefault();
        this.change(change.dataset.mwCartChange, Number(change.dataset.quantity), change.dataset.mwQtyDir);
        return;
      }

      const remove = event.target.closest("[data-mw-discount-remove]");
      if (remove) {
        event.preventDefault();
        const target = remove.dataset.mwDiscountRemove.toLowerCase();
        this.updateDiscounts(this.currentCodes().filter((code) => code.toLowerCase() !== target));
      }
    });

    this.root.addEventListener("change", (event) => {
      const input = event.target.closest("[data-mw-cart-qty-input]");
      if (!input) return;
      const quantity = Math.max(0, parseInt(input.value, 10) || 0);
      this.change(input.dataset.mwCartQtyInput, quantity);
    });

    this.root.addEventListener("keydown", (event) => {
      // Enter inside a quantity input would submit the whole cart form.
      if (event.key === "Enter" && event.target.matches("[data-mw-cart-qty-input]")) {
        event.preventDefault();
        event.target.blur();
      }
    });

    this.root.addEventListener("input", (event) => {
      if (!event.target.matches("[data-mw-cart-note]")) return;
      window.clearTimeout(this.noteTimer);
      const note = event.target.value;
      this.noteTimer = window.setTimeout(() => this.saveNote(note), 500);
    });

    this.root.addEventListener("submit", (event) => {
      const form = event.target.closest("[data-mw-cart-discount]");
      if (!form) return;
      event.preventDefault();
      const input = form.querySelector("[data-mw-cart-discount-input]");
      const code = (input && input.value ? input.value : "").trim();
      if (!code) return;
      const codes = this.currentCodes();
      if (!codes.some((item) => item.toLowerCase() === code.toLowerCase())) codes.push(code);
      this.updateDiscounts(codes);
    });

    document.addEventListener("mw:cart:updated", () => this.refresh());
  }

  currentCodes() {
    return Array.from(this.root.querySelectorAll("[data-mw-discount-code]")).map((item) => item.dataset.mwDiscountCode);
  }

  change(key, quantity, direction) {
    return this.request("/cart/change.js", { id: key, quantity }, { key, direction });
  }

  updateDiscounts(codes) {
    return this.request("/cart/update.js", { discount: codes.join(",") });
  }

  async saveNote(note) {
    try {
      await fetch(this.route("/cart/update.js"), {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ note })
      });
    } catch (error) {}
  }

  async request(path, body, focus) {
    this.setLoading(true);
    this.clearMessage();

    try {
      const response = await fetch(this.route(path), {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(Object.assign({}, body, {
          sections: this.sectionsToRender(),
          sections_url: window.location.pathname
        }))
      });
      const payload = await response.json();
      if (!response.ok) throw payload;

      await this.render(payload.sections);
      this.restoreFocus(focus);
    } catch (error) {
      this.showMessage(error.description || error.message || this.root.dataset.errorMessage);
      await this.refresh();
    } finally {
      this.setLoading(false);
    }
  }

  async render(sections) {
    if (!sections) {
      await this.refresh();
      const drawer = this.drawer();
      if (drawer) await drawer.renderSections(null);
      return;
    }

    this.applyPage(sections[this.sectionId]);
    const drawer = this.drawer();
    if (drawer) {
      drawer.applyCartSection(sections[drawer.sectionId]);
      drawer.applyHeaderSection(sections[drawer.headerSectionId()]);
    }
  }

  async refresh() {
    try {
      const response = await fetch(this.route(`/cart?section_id=${encodeURIComponent(this.sectionId)}`));
      if (response.ok) this.applyPage(await response.text());
    } catch (error) {}
  }

  // Replaces only the dynamic parts while the layout is the same, so the note,
  // accelerated checkout buttons and focus outside them survive the update.
  applyPage(html) {
    if (!html) return;
    const next = new DOMParser().parseFromString(html, "text/html").querySelector("[data-mw-cart-page-content]");
    const current = this.content;
    if (!next || !current) return;

    const currentParts = Array.from(current.querySelectorAll("[data-mw-cart-part]"));
    const nextParts = Array.from(next.querySelectorAll("[data-mw-cart-part]"));
    const sameLayout = currentParts.length > 0
      && currentParts.length === nextParts.length
      && currentParts.every((part, index) => part.dataset.mwCartPart === nextParts[index].dataset.mwCartPart);

    if (sameLayout) {
      currentParts.forEach((part, index) => part.replaceWith(nextParts[index]));
    } else {
      current.innerHTML = next.innerHTML;
    }
  }

  restoreFocus(focus) {
    if (!focus || !focus.key) return;
    const line = Array.from(this.root.querySelectorAll("[data-mw-cart-line]")).find((item) => item.dataset.mwCartLine === focus.key);
    const target = line
      ? line.querySelector(focus.direction ? `[data-mw-qty-dir="${focus.direction}"]` : "[data-mw-cart-qty-input]")
      : this.root.querySelector("[data-mw-cart-line] [data-mw-qty-dir], .mw-cart-empty a");
    if (target) target.focus({ preventScroll: true });
  }

  setLoading(isLoading) {
    this.root.classList.toggle("is-loading", isLoading);
    this.root.setAttribute("aria-busy", isLoading ? "true" : "false");
  }

  showMessage(message) {
    const element = this.root.querySelector("[data-mw-cart-page-message]");
    if (!element || !message) return;
    element.textContent = message;
    element.dataset.state = "error";
    element.hidden = false;
  }

  clearMessage() {
    const element = this.root.querySelector("[data-mw-cart-page-message]");
    if (!element) return;
    element.textContent = "";
    element.hidden = true;
  }
}

function mwFormatMoney(cents, format) {
  if (window.Shopify && typeof window.Shopify.formatMoney === "function") {
    return window.Shopify.formatMoney(cents, format);
  }
  const value = Number(cents || 0) / 100;
  const withDecimals = value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const noDecimals = Math.round(value).toLocaleString("en-US");
  return (format || "${{amount}}")
    .replace(/\{\{\s*amount_no_decimals\s*\}\}/, noDecimals)
    .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, withDecimals.replace(/,/g, "_").replace(".", ",").replace(/_/g, "."))
    .replace(/\{\{\s*amount\s*\}\}/, withDecimals);
}

// Recently viewed products, newest first. Written on the PDP, read by the collection banner.
const MWRecentlyViewed = {
  key: "mw:recently-viewed",
  max: 12,

  read() {
    try {
      const list = JSON.parse(window.localStorage.getItem(this.key) || "[]");
      return Array.isArray(list) ? list : [];
    } catch (error) {
      return [];
    }
  },

  write(list) {
    try {
      window.localStorage.setItem(this.key, JSON.stringify(list.slice(0, this.max)));
    } catch (error) {}
  },

  record(handle, collections) {
    if (!handle) return;
    const list = this.read().filter((item) => item.handle !== handle);
    list.unshift({ handle, collections, t: Date.now() });
    this.write(list);
  },

  remove(handle) {
    this.write(this.read().filter((item) => item.handle !== handle));
  }
};

class MWTrackView {
  constructor(root) {
    const collections = (root.dataset.collections || "").split(",").filter(Boolean);
    MWRecentlyViewed.record(root.dataset.mwTrackView, collections);
  }
}

// Collection banner: swaps the fallback product for the visitor's last viewed one.
class MWCollectionBannerProduct {
  constructor(media) {
    this.media = media;
    this.section = media.closest(".mw-cbanner");
    this.moneyFormat = (this.section && this.section.dataset.moneyFormat) || "${{amount}}";
    this.load();
  }

  // The most recent view wins: sort by timestamp instead of trusting the stored order.
  // (Not rendered at all when the collection has a promotion product, see the section.)
  candidate() {
    const scope = this.media.dataset.scope;
    const collection = this.media.dataset.collection;
    return MWRecentlyViewed.read()
      .filter((item) => scope !== "collection" || (item.collections || []).includes(collection))
      .sort((a, b) => (Number(b.t) || 0) - (Number(a.t) || 0))[0];
  }

  route(path) {
    const root = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : "/";
    return `${root.replace(/\/$/, "")}${path}`;
  }

  async load() {
    const item = this.candidate();
    // Nothing viewed yet, or it is already the server-rendered product.
    if (!item || item.handle === this.media.dataset.current) {
      this.done();
      return;
    }

    try {
      const response = await fetch(this.route(`/products/${encodeURIComponent(item.handle)}.js`));
      if (!response.ok) {
        if (response.status === 404) MWRecentlyViewed.remove(item.handle);
        return;
      }
      const product = await response.json();
      const image = this.pickImage(product);
      if (image) await this.render(product, image);
    } catch (error) {
      // Keep the fallback product.
    } finally {
      this.done();
    }
  }

  pickImage(product) {
    const images = (product.media || [])
      .map((media) => media.preview_image)
      .filter((image) => image && image.src);
    if (!images.length && product.featured_image) return { src: product.featured_image };
    const position = Math.max(1, parseInt(this.media.dataset.mediaPosition, 10) || 1);
    return images[position - 1] || images[0];
  }

  sized(src, width) {
    try {
      const url = new URL(src, window.location.origin);
      url.searchParams.set("width", width);
      return url.toString();
    } catch (error) {
      return src;
    }
  }

  async render(product, image) {
    const style = this.media.dataset.mediaStyle || "frame";
    const frame = document.createElement("div");
    frame.className = `mw-hero__media-frame mw-cbanner__frame mw-cbanner__frame--${style}`;

    const link = document.createElement("a");
    link.className = "mw-cbanner__product-link";
    link.href = product.url;
    link.setAttribute("aria-label", product.title);

    const img = document.createElement("img");
    img.src = this.sized(image.src, 1100);
    img.srcset = [520, 760, 1100, 1400].map((width) => `${this.sized(image.src, width)} ${width}w`).join(", ");
    img.sizes = "(min-width: 990px) 40vw, 92vw";
    img.alt = product.title;
    if (image.width) img.width = image.width;
    if (image.height) img.height = image.height;
    link.appendChild(img);
    frame.appendChild(link);

    if (this.media.dataset.showCard === "true") {
      const card = document.createElement("a");
      card.className = "mw-cbanner__card";
      card.href = product.url;

      const title = document.createElement("span");
      title.className = "mw-cbanner__card-title";
      title.textContent = product.title;

      const price = document.createElement("span");
      price.className = "mw-cbanner__card-price";
      // The shop money format can include markup (e.g. <span class=money>), so it is
      // inserted as HTML. It comes from the store settings and the amount is numeric.
      price.innerHTML = mwFormatMoney(product.price, this.moneyFormat);
      if (product.compare_at_price > product.price) {
        const compare = document.createElement("s");
        compare.innerHTML = mwFormatMoney(product.compare_at_price, this.moneyFormat);
        price.appendChild(compare);
      }

      card.append(title, price);

      if (this.media.dataset.cardLabel) {
        const cta = document.createElement("span");
        cta.className = "mw-cbanner__card-cta";
        cta.textContent = `${this.media.dataset.cardLabel} →`;
        card.appendChild(cta);
      }
      frame.appendChild(card);
    }

    // Swap only once the new image is ready, so the frame never shows empty.
    if (img.decode) await img.decode().catch(() => {});

    this.media.replaceChildren(frame);
    this.media.hidden = false;
    this.media.dataset.current = product.handle;

    if (this.section) {
      this.section.querySelectorAll("[data-mw-cbanner-product-url]").forEach((button) => {
        button.href = product.url;
      });
    }
  }

  done() {
    this.media.classList.remove("is-pending");
  }
}

class MWCollectionBanner {
  constructor(root) {
    this.root = root;
    this.place = this.place.bind(this);
    this.place();
    window.addEventListener("resize", this.debounce(this.place, 150), { passive: true });
  }

  place() {
    const banner = this.root.querySelector("[data-mw-grid-banner]");
    if (!banner) return;
    const columns = getComputedStyle(this.root).gridTemplateColumns.split(" ").filter(Boolean).length || 1;
    const cards = Array.from(this.root.children).filter((child) => child !== banner && child.matches("[data-mw-grid-card]"));
    const reference = cards[columns];
    if (reference) this.root.insertBefore(banner, reference);
    else this.root.appendChild(banner);
  }

  debounce(callback, wait) {
    let timer;
    return () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(callback, wait);
    };
  }
}

class MWNewsletter {
  constructor(root) {
    this.root = root;
    this.toast = root.querySelector("[data-mw-toast]");
    this.bind();
  }

  bind() {
    this.root.addEventListener("submit", (event) => {
      if (!this.root.matches("[data-mw-newsletter]")) return;
      if (!this.root.action.includes("/contact")) event.preventDefault();
      if (this.toast) {
        this.toast.hidden = false;
        window.setTimeout(() => {
          this.toast.hidden = true;
        }, 2800);
      }
    });
  }
}

class MWDiscountPopup {
  constructor(root) {
    this.root = root;
    this.sectionId = root.dataset.sectionId;
    this.storageKey = `mw-discount-popup:${this.sectionId}`;
    this.form = root.querySelector("[data-mw-popup-form]");
    this.email = root.querySelector("[data-mw-popup-email]");
    this.error = root.querySelector("[data-mw-popup-error]");
    this.submit = root.querySelector("[data-mw-popup-submit]");
    this.teaser = root.querySelector("[data-mw-popup-teaser]");
    this.dialog = root.querySelector(".mw-popup__dialog");
    this.designMode = root.dataset.designMode === "true";
    this.lastFocus = null;
    this.opened = false;
    this.handleKeydown = this.handleKeydown.bind(this);
    this.bind();

    if (this.designMode) {
      if (root.dataset.previewSuccess === "true") this.showStep("success");
      return;
    }

    // Native (non-AJAX) fallback: Shopify reloads the page with customer_posted=true.
    if (root.querySelector("[data-mw-popup-posted]") && this.read("pending")) {
      this.write({ subscribed: true, pending: false });
      this.showStep("success");
      this.open();
      return;
    }

    if (this.read("subscribed")) return;
    if (this.isSnoozed()) {
      this.showTeaser();
      return;
    }
    this.schedule();
  }

  bind() {
    this.root.querySelectorAll("[data-mw-popup-close]").forEach((item) => {
      item.addEventListener("click", () => this.close());
    });
    this.root.querySelectorAll("[data-mw-popup-open]").forEach((item) => {
      item.addEventListener("click", () => this.open());
    });

    const teaserClose = this.root.querySelector("[data-mw-popup-teaser-close]");
    if (teaserClose) {
      teaserClose.addEventListener("click", () => {
        this.hideTeaser();
        try { window.sessionStorage.setItem(`${this.storageKey}:teaser`, "hidden"); } catch (error) {}
      });
    }

    const copy = this.root.querySelector("[data-mw-popup-copy]");
    if (copy) copy.addEventListener("click", () => this.copyCode(copy));

    if (this.form) this.form.addEventListener("submit", (event) => this.onSubmit(event));
  }

  // ---- triggers -------------------------------------------------------

  schedule() {
    const trigger = this.root.dataset.trigger;
    const delay = (parseInt(this.root.dataset.delay, 10) || 0) * 1000;
    const desktop = window.matchMedia("(hover: hover) and (pointer: fine)").matches;

    if (trigger === "scroll") {
      const percent = parseInt(this.root.dataset.scroll, 10) || 40;
      const onScroll = () => {
        const max = document.documentElement.scrollHeight - window.innerHeight;
        if (max <= 0 || (window.scrollY / max) * 100 >= percent) {
          window.removeEventListener("scroll", onScroll);
          this.open();
        }
      };
      window.addEventListener("scroll", onScroll, { passive: true });
      return;
    }

    if (trigger === "exit" && desktop) {
      const onLeave = (event) => {
        if (event.clientY > 0 || event.relatedTarget) return;
        document.removeEventListener("mouseout", onLeave);
        this.open();
      };
      document.addEventListener("mouseout", onLeave);
      return;
    }

    window.setTimeout(() => this.open(), delay);
  }

  isSnoozed() {
    const days = parseInt(this.root.dataset.frequency, 10) || 0;
    if (days === 0) {
      try { return window.sessionStorage.getItem(this.storageKey) === "closed"; } catch (error) { return false; }
    }
    const closedAt = Number(this.read("closedAt") || 0);
    return closedAt > 0 && Date.now() - closedAt < days * 86400000;
  }

  // ---- open / close ---------------------------------------------------

  open() {
    if (this.opened) return;
    if (!this.designMode && document.body.classList.contains("mw-drawer-open")) {
      // Never stack on top of the cart / menu drawers; retry shortly.
      window.setTimeout(() => this.open(), 4000);
      return;
    }
    this.opened = true;
    this.lastFocus = document.activeElement;
    this.hideTeaser();
    this.root.classList.add("is-open");
    this.root.removeAttribute("aria-hidden");
    document.body.classList.add("mw-popup-open");
    document.addEventListener("keydown", this.handleKeydown);
    const focusTarget = this.root.querySelector(".mw-popup__step:not([hidden]) input, .mw-popup__step:not([hidden]) button");
    if (focusTarget && !this.designMode) window.setTimeout(() => focusTarget.focus({ preventScroll: true }), 120);
  }

  close() {
    if (!this.opened) return;
    this.opened = false;
    this.root.classList.remove("is-open");
    this.root.setAttribute("aria-hidden", "true");
    document.body.classList.remove("mw-popup-open");
    document.removeEventListener("keydown", this.handleKeydown);
    if (this.designMode) return;

    if (!this.read("subscribed")) {
      this.write({ closedAt: Date.now() });
      try { window.sessionStorage.setItem(this.storageKey, "closed"); } catch (error) {}
      this.showTeaser();
    }
    if (this.lastFocus && typeof this.lastFocus.focus === "function") this.lastFocus.focus({ preventScroll: true });
  }

  handleKeydown(event) {
    if (event.key === "Escape") {
      this.close();
      return;
    }
    if (event.key !== "Tab" || !this.dialog) return;
    const focusable = Array.from(this.dialog.querySelectorAll("a[href], button:not([disabled]), input:not([type='hidden']):not([disabled])"))
      .filter((item) => item.offsetParent !== null);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  showTeaser() {
    if (!this.teaser || this.designMode) return;
    try {
      if (window.sessionStorage.getItem(`${this.storageKey}:teaser`) === "hidden") return;
    } catch (error) {}
    this.teaser.hidden = false;
  }

  hideTeaser() {
    if (this.teaser) this.teaser.hidden = true;
  }

  showStep(name) {
    this.root.querySelectorAll("[data-mw-popup-step]").forEach((step) => {
      step.hidden = step.dataset.mwPopupStep !== name;
    });
  }

  // ---- submit ---------------------------------------------------------

  async onSubmit(event) {
    event.preventDefault();
    const value = (this.email.value || "").trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      this.showError(this.error.dataset.invalidText);
      this.email.focus();
      return;
    }

    this.clearError();
    this.setLoading(true);

    try {
      const response = await fetch(this.form.action, {
        method: "POST",
        body: new FormData(this.form),
        headers: { Accept: "text/html" }
      });
      const url = response.url || "";

      // Shopify bot protection: let the browser complete the challenge natively.
      if (url.includes("/challenge")) {
        this.write({ pending: true });
        HTMLFormElement.prototype.submit.call(this.form);
        return;
      }

      if (url.includes("customer_posted=true")) {
        this.success();
        return;
      }

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, "text/html");
      const remoteForm = this.form.id ? doc.getElementById(this.form.id) : null;
      const errors = remoteForm && remoteForm.querySelector(".errors");
      if (errors && errors.textContent.trim()) {
        this.showError(errors.textContent.trim());
      } else {
        // Shopify answers 200 without errors when the email already exists: still reveal the code.
        this.success();
      }
    } catch (error) {
      this.write({ pending: true });
      HTMLFormElement.prototype.submit.call(this.form);
    } finally {
      this.setLoading(false);
    }
  }

  success() {
    this.write({ subscribed: true, pending: false });
    this.showStep("success");
    const copy = this.root.querySelector("[data-mw-popup-copy]");
    if (copy) copy.focus({ preventScroll: true });
    document.dispatchEvent(new CustomEvent("mw:popup:subscribed", { detail: { sectionId: this.sectionId } }));
  }

  async copyCode(button) {
    const code = this.root.querySelector("[data-mw-popup-code]");
    if (!code) return;
    const text = code.textContent.trim();
    try {
      await navigator.clipboard.writeText(text);
    } catch (error) {
      const range = document.createRange();
      range.selectNodeContents(code);
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(range);
      try { document.execCommand("copy"); } catch (copyError) {}
    }
    const original = button.dataset.originalText || button.textContent;
    button.dataset.originalText = original;
    button.textContent = button.dataset.copiedText || original;
    button.classList.add("is-copied");
    window.setTimeout(() => {
      button.textContent = original;
      button.classList.remove("is-copied");
    }, 2200);
  }

  setLoading(isLoading) {
    if (!this.submit) return;
    this.submit.disabled = isLoading;
    this.submit.classList.toggle("is-loading", isLoading);
  }

  showError(message) {
    if (!this.error) return;
    this.error.textContent = message;
    this.error.hidden = false;
    this.email.setAttribute("aria-invalid", "true");
  }

  clearError() {
    if (!this.error) return;
    this.error.hidden = true;
    this.error.textContent = "";
    this.email.removeAttribute("aria-invalid");
  }

  // ---- storage --------------------------------------------------------

  read(key) {
    try {
      const data = JSON.parse(window.localStorage.getItem(this.storageKey) || "{}");
      return data[key];
    } catch (error) {
      return undefined;
    }
  }

  write(values) {
    try {
      const data = JSON.parse(window.localStorage.getItem(this.storageKey) || "{}");
      window.localStorage.setItem(this.storageKey, JSON.stringify(Object.assign(data, values)));
    } catch (error) {}
  }
}

class MWAutoSubmit {
  constructor(root) {
    this.root = root;
    this.bind();
  }

  bind() {
    const onChange = (event) => {
      if (!event.target.matches("[data-mw-submit-on-change]")) return;
      if (this.root.requestSubmit) this.root.requestSubmit();
      else this.root.submit();
    };

    this.root.addEventListener("change", onChange);

    // Controls rendered outside the form (e.g. the filters drawer) join it via the form attribute.
    if (this.root.id) {
      document.querySelectorAll(`[data-mw-form-for="${this.root.id}"]`).forEach((container) => {
        container.addEventListener("change", onChange);
      });
    }
  }
}

function mwInit(root = document) {
  root.querySelectorAll("[data-mw-drawer]").forEach((item) => {
    if (!item.mwDrawer) item.mwDrawer = new MWDrawer(item);
  });
  root.querySelectorAll("[data-mw-header]").forEach((item) => {
    if (!item.mwHeader) item.mwHeader = new MWHeader(item);
  });
  root.querySelectorAll("[data-mw-mega-nav]").forEach((item) => {
    if (!item.mwMegaNav) item.mwMegaNav = new MWMegaNav(item);
  });
  root.querySelectorAll("[data-mw-hero-slider]").forEach((item) => {
    if (!item.mwHeroSlider) item.mwHeroSlider = new MWHeroSlider(item);
  });
  root.querySelectorAll("[data-mw-drag]").forEach((item) => {
    if (!item.mwDragRail) item.mwDragRail = new MWDragRail(item);
  });
  root.querySelectorAll("[data-mw-accordion]").forEach((item) => {
    if (!item.mwAccordion) item.mwAccordion = new MWAccordion(item);
  });
  root.querySelectorAll("[data-mw-tabs]").forEach((item) => {
    if (!item.mwTabs) item.mwTabs = new MWTabs(item);
  });
  root.querySelectorAll("[data-mw-acc]").forEach((item) => {
    if (!item.mwAccordion) item.mwAccordion = new MWProductAccordion(item);
  });
  root.querySelectorAll("[data-mw-quantity]").forEach((item) => {
    if (!item.mwQuantity) item.mwQuantity = new MWQuantity(item);
  });
  root.querySelectorAll("[data-mw-gallery]").forEach((item) => {
    if (!item.mwProductGallery) item.mwProductGallery = new MWProductGallery(item);
  });
  root.querySelectorAll("[data-mw-product]").forEach((item) => {
    if (!item.mwProductForm) item.mwProductForm = new MWProductForm(item);
  });
  root.querySelectorAll("[data-mw-product-card]").forEach((item) => {
    if (!item.mwProductCard) item.mwProductCard = new MWProductCard(item);
  });
  root.querySelectorAll("[data-mw-cart-root]").forEach((item) => {
    if (!item.mwCart) item.mwCart = new MWCartDrawer(item);
  });
  root.querySelectorAll("[data-mw-grid-with-banner]").forEach((item) => {
    if (!item.mwCollectionBanner) item.mwCollectionBanner = new MWCollectionBanner(item);
  });
  root.querySelectorAll("[data-mw-newsletter]").forEach((item) => {
    if (!item.mwNewsletter) item.mwNewsletter = new MWNewsletter(item);
  });
  root.querySelectorAll("[data-mw-auto-submit]").forEach((item) => {
    if (!item.mwAutoSubmit) item.mwAutoSubmit = new MWAutoSubmit(item);
  });
  root.querySelectorAll("[data-mw-track-view]").forEach((item) => {
    if (!item.mwTrackView) item.mwTrackView = new MWTrackView(item);
  });
  root.querySelectorAll("[data-mw-cbanner-last-viewed]").forEach((item) => {
    if (!item.mwBannerProduct) item.mwBannerProduct = new MWCollectionBannerProduct(item);
  });
  root.querySelectorAll("[data-mw-cart-page]").forEach((item) => {
    if (!item.mwCartPage) item.mwCartPage = new MWCartPage(item);
  });
  root.querySelectorAll("[data-mw-discount-popup]").forEach((item) => {
    if (!item.mwDiscountPopup) item.mwDiscountPopup = new MWDiscountPopup(item);
  });
}

document.addEventListener("DOMContentLoaded", () => mwInit());
document.addEventListener("shopify:section:load", (event) => mwInit(event.target));

// Theme editor: open the popup while its section is selected.
document.addEventListener("shopify:section:select", (event) => {
  const popup = event.target.querySelector("[data-mw-discount-popup]");
  if (popup && popup.mwDiscountPopup) popup.mwDiscountPopup.open();
});
document.addEventListener("shopify:section:deselect", (event) => {
  const popup = event.target.querySelector("[data-mw-discount-popup]");
  if (popup && popup.mwDiscountPopup) popup.mwDiscountPopup.close();
});

// Theme editor: reveal the selected product tab block.
document.addEventListener("shopify:block:select", (event) => {
  if (!event.target.closest) return;
  const panel = event.target.closest("[data-mw-tab-panel]");
  const tabs = panel && panel.closest("[data-mw-tabs]");
  if (tabs && tabs.mwTabs) tabs.mwTabs.activateById(panel.dataset.mwTabPanel);

  // Accordion tab selected in the editor: open it.
  const item = event.target.closest("[data-mw-acc-item]");
  const accordion = item && item.closest("[data-mw-acc]");
  if (accordion && accordion.mwAccordion) accordion.mwAccordion.open(item);
});

window.MWTheme = {
  init: mwInit,
  drawers: MWDrawer,
  productCard: MWProductCard,
  cart: MWCartDrawer
};
